import { useState } from 'react'
import type {
  CharacterSheet,
  InventoryAction,
  InventoryEntry,
  InventoryProblem,
} from '@rollcall/contracts'
import { isEquippable } from '@rollcall/rules'
import { Description } from './description.js'
import { useT } from './i18n-context.js'
import { contentName } from './labels.js'

/**
 * Вкладка «Инвентарь».
 *
 * Ни одного расчёта здесь нет, как и на вкладке «Персонаж»: список,
 * вес, порог переносимого и признак перегруза приходят готовыми
 * в составе листа (D4, свёртка журнала). Порог не константа
 * в клиенте — он посчитан по таблице грузоподъёмности из записи
 * правил, и клиент его только показывает.
 *
 * Каждое действие — команда на сервер (D3): «надень кольчугу»,
 * а не «КД теперь 16». Клиент не правит список у себя даже
 * на мгновение: показывается то, что вернул сервер. Иначе после
 * отказа на экране осталось бы состояние, которого нет ни в одном
 * журнале, — и разошлось бы оно молча.
 *
 * Надетое стоит отдельно от остального, потому что это два разных
 * вопроса: «что на мне» смотрят в бою, «что в сумке» — между боями.
 * Монеты — своей строкой: это не предмет, у них нет ни веса записи,
 * ни возможности их надеть.
 */

export type InventoryTabProps = {
  readonly sheet: CharacterSheet
  /** Команда серверу; список обновится ответом, а не отсюда. */
  readonly onAction: (action: InventoryAction) => void
  /** Предмет, по которому ждём ответ: его действия заблокированы. */
  readonly pending: string | null
  /** Отказ сервера: показывается у строки предмета, а не полосой сверху. */
  readonly problems: readonly InventoryProblem[]
  /** Сеть или пятисотка — это не про предмет, это про связь. */
  readonly failure: string | null
}

/** Скорость с листа: её считает движок, а не вкладка. */
function speedOf(sheet: CharacterSheet): number | string {
  const value = sheet.values['speed']?.value
  return typeof value === 'number' ? value : '—'
}

/** Число фунтов человеку: целое без хвоста, дробное — как есть. */
function amount(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100)
}

export function InventoryTab({
  sheet,
  onAction,
  pending,
  problems,
  failure,
}: InventoryTabProps) {
  const t = useT()
  const { inventory } = sheet
  const equipped = inventory.entries.filter((entry) => entry.equipped)
  const carried = inventory.entries.filter((entry) => !entry.equipped)

  return (
    <section className="app-screen" aria-labelledby="inventory-title">
      <h2 id="inventory-title">{t('inventory.title')}</h2>

      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}

      <section aria-labelledby="inventory-load">
        <h3 id="inventory-load">{t('inventory.weightTotal')}</h3>
        <p className="app-inventory-load" data-testid="inventory-load">
          {t('inventory.weightOf', {
            total: amount(inventory.totalWeightLb),
            carry: amount(inventory.carryLb),
          })}
        </p>
        {inventory.overCarry ? (
          <p className="app-problems" role="alert" data-testid="inventory-over">
            {/* Скорость здесь — та, что на листе: правило «не больше
                пяти футов» применил движок, и повторять его число
                в клиенте значило бы завести второй источник. */}
            {t('inventory.over', {
              carry: amount(inventory.carryLb),
              speed: speedOf(sheet),
            })}
          </p>
        ) : (
          <p className="app-slot-hint">
            {t('inventory.carryHint', { carry: amount(inventory.carryLb) })}
          </p>
        )}
        <p className="app-slot-hint">
          {t('inventory.dragHint', { drag: amount(inventory.dragLb) })}
        </p>

        {/* Монеты — отдельной строкой: они приходят из стартового
            набора («and 19 GP») и предметом не являются. */}
        <p className="app-inventory-coins" data-testid="inventory-coins">
          {t('inventory.coins')}
          {': '}
          {t('inventory.coinsValue', { count: inventory.coinsGp })}
        </p>

        {/* Позиции без веса не молчат: иначе итог был бы занижен,
            а почему — не увидел бы никто (REPORT, раздел 3). */}
        {inventory.weightUnknown.length > 0 && (
          <p className="app-slot-hint" data-testid="inventory-unknown-weight">
            {t('inventory.unknownWeight', {
              items: inventory.weightUnknown.join(', '),
            })}
          </p>
        )}
      </section>

      {inventory.entries.length === 0 ? (
        <p>{t('inventory.empty')}</p>
      ) : (
        <>
          <Group
            id="inventory-equipped"
            title={t('inventory.equipped')}
            entries={equipped}
            onAction={onAction}
            pending={pending}
            problems={problems}
          />
          <Group
            id="inventory-carried"
            title={t('inventory.carried')}
            entries={carried}
            onAction={onAction}
            pending={pending}
            problems={problems}
          />
        </>
      )}
    </section>
  )
}

function Group({
  id,
  title,
  entries,
  onAction,
  pending,
  problems,
}: {
  readonly id: string
  readonly title: string
  readonly entries: readonly InventoryEntry[]
  readonly onAction: (action: InventoryAction) => void
  readonly pending: string | null
  readonly problems: readonly InventoryProblem[]
}) {
  if (entries.length === 0) return null

  return (
    <section aria-labelledby={id}>
      <h3 id={id}>{title}</h3>
      <ul className="app-list app-inventory">
        {entries.map((entry) => (
          <Row
            key={entry.id ?? entry.name}
            entry={entry}
            onAction={onAction}
            pending={pending}
            problem={
              problems.find((item) => item.item === (entry.id ?? entry.name)) ?? null
            }
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * Строка предмета: название по-русски, количество, вес и действия.
 *
 * Действия видны только у того, у чего они есть: позицию без записи
 * контента («Spellbook» стартового набора) не на что сослать, и
 * кнопка, которая заведомо получит отказ, здесь была бы обманом.
 *
 * Количество меняется прямо в строке — «на одну больше», «на одну
 * меньше»: отдельное поле с кнопкой «задать» требовало трёх действий
 * там, где нужно одно. Число между кнопками — то, что вернул сервер.
 *
 * «Выбросить» спрашивает подтверждение: это единственное действие,
 * которое нельзя отменить нажатием напротив.
 */
function Row({
  entry,
  onAction,
  pending,
  problem,
}: {
  readonly entry: InventoryEntry
  readonly onAction: (action: InventoryAction) => void
  readonly pending: string | null
  readonly problem: InventoryProblem | null
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [dropping, setDropping] = useState(false)
  const id = entry.id
  const busy = id !== null && pending === id
  const name = id === null ? entry.name : contentName(t, id, entry.name)
  const weight = entry.weightLb === null ? null : entry.weightLb * entry.quantity

  const note = [
    weight === null ? null : t('inventory.weight', { count: amount(weight) }),
  ].filter((part): part is string => part !== null)

  return (
    <li className="app-inventory-row" data-item={id ?? ''}>
      <div className="app-inventory-head">
        <span className="app-value-label">{name}</span>
        {note.length > 0 && <span className="app-value-note">{note.join(' · ')}</span>}
      </div>

      {id === null ? (
        <p className="app-slot-hint">{t('inventory.noRecord')}</p>
      ) : (
        <>
          <div className="app-inventory-actions">
            {/* Количество — прямо в строке: «−», число, «+». */}
            <span className="app-inventory-count" data-testid={`count-${id}`}>
              <button
                type="button"
                className="app-icon-button"
                aria-label={t('inventory.less', { name })}
                disabled={busy || entry.quantity <= 1}
                onClick={() => {
                  onAction({ kind: 'set', item: id, count: entry.quantity - 1 })
                }}
              >
                −
              </button>
              <span className="app-value-number">{entry.quantity}</span>
              <button
                type="button"
                className="app-icon-button"
                aria-label={t('inventory.more', { name })}
                disabled={busy}
                onClick={() => {
                  onAction({ kind: 'take', item: id, count: 1 })
                }}
              >
                +
              </button>
            </span>

            {(entry.equipped || isEquippable(entry.category)) && (
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy}
                onClick={() => {
                  onAction({ kind: entry.equipped ? 'unequip' : 'equip', item: id })
                }}
              >
                {t(entry.equipped ? 'inventory.unequip' : 'inventory.equip')}
              </button>
            )}

            <button
              type="button"
              className="app-button app-button-quiet"
              disabled={busy}
              aria-expanded={dropping}
              onClick={() => {
                setDropping(!dropping)
              }}
            >
              {t('inventory.drop')}
            </button>

            {entry.text !== null && (
              <button
                type="button"
                className="app-button app-button-quiet"
                aria-expanded={open}
                onClick={() => {
                  setOpen(!open)
                }}
              >
                {t('inventory.details')}
              </button>
            )}
          </div>

          {dropping && (
            <div className="app-inventory-drop" data-testid={`drop-${id}`}>
              <p className="app-slot-hint">
                {t('inventory.dropConfirm', { name, count: entry.quantity })}
              </p>
              <div className="app-actions">
                {entry.quantity > 1 && (
                  <button
                    type="button"
                    className="app-button app-button-quiet"
                    disabled={busy}
                    onClick={() => {
                      onAction({ kind: 'set', item: id, count: entry.quantity - 1 })
                      setDropping(false)
                    }}
                  >
                    {t('inventory.dropOne')}
                  </button>
                )}
                <button
                  type="button"
                  className="app-button"
                  disabled={busy}
                  onClick={() => {
                    onAction({ kind: 'drop', item: id })
                    setDropping(false)
                  }}
                >
                  {t('inventory.dropAll')}
                </button>
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  onClick={() => {
                    setDropping(false)
                  }}
                >
                  {t('inventory.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Описание: русское основное, оригинал по кнопке (D32). */}
          {open && entry.text !== null && (
            <Description text={entry.text} textRu={entry.textRu} />
          )}
        </>
      )}

      {problem !== null && (
        <p className="app-problems" role="alert">
          {t(`inventory.refusal.${problem.code}`, {
            blockedBy:
              problem.blockedBy === null ? '' : contentName(t, problem.blockedBy),
          })}
        </p>
      )}
    </li>
  )
}
